// Authentication method types for project configuration

export type AuthMethod =
  | 'email_password'
  | 'magic_link'
  | 'email_otp'
  | 'google'
  | 'github'
  | 'microsoft'
  | 'okta'
  | 'apple';

export const AUTH_METHODS: AuthMethod[] = [
  'email_password',
  'magic_link',
  'email_otp',
  'google',
  'github',
  'microsoft',
  'okta',
  'apple',
];

export const OAUTH_AUTH_METHODS: AuthMethod[] = [
  'google',
  'github',
  'microsoft',
  'okta',
  'apple',
];

export const AUTH_METHOD_DISPLAY_NAMES: Record<AuthMethod, string> = {
  email_password: 'Email & Password',
  magic_link: 'Magic Link',
  email_otp: 'Email OTP',
  google: 'Google',
  github: 'GitHub',
  microsoft: 'Microsoft',
  okta: 'Okta',
  apple: 'Apple',
};

export const AUTH_METHOD_DESCRIPTIONS: Record<AuthMethod, string> = {
  email_password: 'Traditional email and password authentication',
  magic_link: 'Passwordless login via email link',
  email_otp: 'One-time password sent to email',
  google: 'Sign in with Google account',
  github: 'Sign in with GitHub account',
  microsoft: 'Sign in with Microsoft account',
  okta: 'Sign in with Okta',
  apple: 'Sign in with Apple ID',
};

export const AUTH_METHOD_ICONS: Record<AuthMethod, string> = {
  email_password: '🔑',
  magic_link: '✉️',
  email_otp: '🔢',
  google: '🔵',
  github: '⚫',
  microsoft: '🟦',
  okta: '🟠',
  apple: '🍎',
};

// Single auth method with configuration details
export interface AuthMethodConfig {
  method: AuthMethod;
  display_name: string;
  description: string;
  enabled: boolean;
  is_oauth: boolean;
  configured: boolean;
  requires_configuration: boolean;
}

// API Response types
export interface AuthMethodsListResponse {
  success: boolean;
  data?: {
    auth_methods: AuthMethodConfig[];
    available_methods: AuthMethod[];
    oauth_providers: AuthMethod[];
  };
  error?: string;
}

export interface AuthMethodsUpdateRequest {
  auth_methods: AuthMethod[];
}

export interface AuthMethodsUpdateResponse {
  success: boolean;
  data?: {
    auth_methods: AuthMethodConfig[];
  };
  message?: string;
  error?: string;
  details?: string[];
}

export interface AuthMethodToggleResponse {
  success: boolean;
  data?: AuthMethodConfig;
  message?: string;
  error?: string;
  details?: string[];
}

// Helper function to check if an auth method is an OAuth provider
export function isOAuthMethod(method: AuthMethod): boolean {
  return OAUTH_AUTH_METHODS.includes(method);
}

// Helper function to get display name for an auth method
export function getAuthMethodDisplayName(method: AuthMethod): string {
  return AUTH_METHOD_DISPLAY_NAMES[method] || method;
}

// Helper function to get description for an auth method
export function getAuthMethodDescription(method: AuthMethod): string {
  return AUTH_METHOD_DESCRIPTIONS[method] || '';
}
